import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetTemplateListQueryKey,
  getGetTemplateQueryKey,
  useGetTemplate,
  useUpdateTemplate,
} from "~/api/custom-reports/custom-reports";
import {
  useListOrganizationCategories,
  useListOrganizationCategoryFolders,
} from "~/api/organization/organization";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { useToast } from "~/hooks/use-toast";
import { DraggableSections } from "./DraggableSections";
import { hasSectionExceedingCharLimit, TemplateSection } from "./helpers";

export enum EditTemplateStep {
  NAME_AND_DEFINITION = 1,
  INSTRUCTIONS = 2,
}

interface EditTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  templateId: string;
}

interface FormData {
  name: string;
  definition: string;
  sections: TemplateSection[];
}

interface TemplateObject {
  name: string;
  definition: string;
  templateSections: TemplateSection[];
}

export function EditTemplateModal({
  open,
  onOpenChange,
  organizationId,
  templateId,
}: EditTemplateModalProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updateTemplate = useUpdateTemplate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetTemplateListQueryKey(organizationId),
        });
        queryClient.invalidateQueries({
          queryKey: getGetTemplateQueryKey(organizationId, templateId),
        });
      },
      onError: () => {
        toast({
          title: intl.formatMessage({
            id: "editTemplate.error",
            defaultMessage: "Failed to save template",
          }),
        });
      },
    },
  });

  const {
    data: templateResponse,
    isLoading,
    isError,
  } = useGetTemplate(organizationId, templateId, {
    query: {
      enabled: open,
    },
  });

  const { data: categoriesData } = useListOrganizationCategories(
    organizationId,
    {
      query: {
        enabled: open,
      },
    }
  );

  const { data: foldersData } = useListOrganizationCategoryFolders(
    organizationId,
    {
      query: {
        enabled: open,
      },
    }
  );

  const availableCategories = React.useMemo(() => {
    if (!categoriesData) return [];
    const folderMap = new Map(foldersData?.map((f) => [f.id, f.name]) ?? []);
    return categoriesData.map((cat) => ({
      id: cat.id,
      name: cat.name,
      color: cat.color,
      folderName: cat.folderId ? folderMap.get(cat.folderId) : undefined,
    }));
  }, [categoriesData, foldersData]);

  useEffect(() => {
    if (open && isError) {
      toast({
        title: intl.formatMessage({
          id: "editTemplate.loadError",
          defaultMessage: "Failed to load template",
        }),
      });
      onOpenChange(false);
    }
  }, [open, isError, toast, intl, onOpenChange]);

  const initialFormData = useMemo<FormData>(() => {
    if (!templateResponse) {
      return { name: "", definition: "", sections: [] };
    }

    const sections = templateResponse.sections.sort(
      (a, b) => a.order - b.order
    );
    return {
      name: templateResponse.name ?? "",
      definition: templateResponse.definition ?? "",
      sections: sections.map((section, index) => ({
        title: section.title,
        purpose: section.purpose,
        prompt: section.prompt,
        order: index,
        sectionType:
          section.contextType === "docList"
            ? "docList"
            : section.contextType === "chronology"
              ? "chronology"
              : "snapshot",
        config: (section as any).config
          ? {
              excludedCategoryNames:
                (section as any).config.excludedCategoryNames ?? [],
              includedCategoryNames:
                (section as any).config.includedCategoryNames ?? undefined,
              prompt: (section as any).config.prompt ?? undefined,
            }
          : undefined,
      })),
    };
  }, [templateResponse]);

  const [step, setStep] = useState<EditTemplateStep>(
    EditTemplateStep.NAME_AND_DEFINITION
  );
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [showError, setShowError] = useState(false);
  const [parsingError, setParsingError] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData(initialFormData);
      setStep(EditTemplateStep.NAME_AND_DEFINITION);
      setShowError(false);
      setParsingError(false);
    }
  }, [open, initialFormData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === EditTemplateStep.NAME_AND_DEFINITION) {
      if (!formData.name.trim() || !formData.definition.trim()) {
        setShowError(true);
        return;
      }
      setShowError(false);
      setStep(EditTemplateStep.INSTRUCTIONS);
    } else {
      if (formData.sections.length === 0) {
        setShowError(true);
        return;
      }

      const hasEmptyFields = formData.sections.some((section) => {
        if (
          section.sectionType === "docList" ||
          section.sectionType === "chronology"
        ) {
          return !section.title;
        }
        return !section.title || !section.purpose || !section.prompt;
      });

      if (hasEmptyFields || hasSectionExceedingCharLimit(formData.sections)) {
        setParsingError(true);
        return;
      }

      const templateObject: TemplateObject = {
        name: formData.name,
        definition: formData.definition,
        templateSections: formData.sections,
      };

      updateTemplate.mutate(
        {
          organizationId,
          templateId,
          data: {
            name: templateObject.name,
            definition: templateObject.definition,
            sections: templateObject.templateSections.map(
              ({ title, purpose, prompt, sectionType, config }) => ({
                title,
                purpose:
                  sectionType === "docList"
                    ? "List all document titles by category"
                    : sectionType === "chronology"
                      ? "Complete date-ordered chronology of all case events"
                      : purpose,
                prompt:
                  sectionType === "docList"
                    ? "docList"
                    : sectionType === "chronology"
                      ? "chronology"
                      : prompt,
                contextType:
                  sectionType === "docList"
                    ? "docList"
                    : sectionType === "chronology"
                      ? "chronology"
                      : "snapshot",
                config: config
                  ? {
                      ...(config.excludedCategoryNames?.length
                        ? {
                            excludedCategoryNames: config.excludedCategoryNames,
                            includedCategoryNames: [
                              ...new Set([
                                ...availableCategories
                                  .filter(
                                    (c) =>
                                      !config.excludedCategoryNames!.includes(
                                        c.name
                                      )
                                  )
                                  .flatMap((c) =>
                                    c.folderName
                                      ? [c.name, c.folderName]
                                      : [c.name]
                                  ),
                              ]),
                            ],
                          }
                        : {}),
                      ...(config.prompt ? { prompt: config.prompt } : {}),
                    }
                  : undefined,
              })
            ),
          },
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            setStep(EditTemplateStep.NAME_AND_DEFINITION);
            setShowError(false);
            setParsingError(false);
          },
        }
      );
    }
  };

  const handleBack = () => {
    setStep(EditTemplateStep.NAME_AND_DEFINITION);
    setShowError(false);
    setParsingError(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep(EditTemplateStep.NAME_AND_DEFINITION);
    setShowError(false);
    setParsingError(false);
  };

  const renderStepContent = () => {
    if (step === EditTemplateStep.NAME_AND_DEFINITION) {
      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="name">
              <FormattedMessage
                id="template.templateName"
                defaultMessage="Template name"
              />
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={intl.formatMessage({
                id: "template.placeholders.name",
              })}
            />
            {!formData.name && showError && (
              <p className="text-sm text-destructive">
                <FormattedMessage
                  id="template.nameRequired"
                  defaultMessage="The name field can't be empty"
                />
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="definition">
              <FormattedMessage
                id="template.definition"
                defaultMessage="Definition"
              />
            </Label>
            <AutoResizeTextarea
              id="definition"
              value={formData.definition}
              onChange={(e) =>
                setFormData({ ...formData, definition: e.target.value })
              }
              placeholder={intl.formatMessage({
                id: "template.placeholders.definition",
              })}
            />
            {!formData.definition && showError && (
              <p className="text-sm text-destructive">
                <FormattedMessage
                  id="template.definitionRequired"
                  defaultMessage="The definition field can't be empty"
                />
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <DraggableSections
          sections={formData.sections}
          onChange={(sections) => {
            setFormData({ ...formData, sections });
            setShowError(false);
            setParsingError(false);
          }}
          showError={showError}
          parsingError={parsingError}
          availableCategories={availableCategories}
        />
      </div>
    );
  };

  const renderFooter = () => {
    if (step === EditTemplateStep.NAME_AND_DEFINITION) {
      return (
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleClose}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" form="edit-template-form">
            <FormattedMessage
              id="template.continue"
              defaultMessage="Continue"
            />
          </Button>
        </div>
      );
    }

    const sectionsOverLimit = hasSectionExceedingCharLimit(formData.sections);
    const isSaving = updateTemplate.isPending;

    return (
      <>
        <Button
          type="button"
          variant="ghost"
          onClick={handleBack}
          className="sm:mr-auto"
          disabled={isSaving}
        >
          <ChevronLeftIcon className="w-4 h-4 mr-2" />
          {intl.formatMessage({ id: "template.back" })}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isSaving}
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          type="submit"
          form="edit-template-form"
          disabled={sectionsOverLimit || isSaving}
        >
          {isSaving ? (
            <FormattedMessage
              id="editTemplate.saving"
              defaultMessage="Saving..."
            />
          ) : (
            <FormattedMessage id="editTemplate.save" defaultMessage="Save" />
          )}
        </Button>
      </>
    );
  };

  return (
    <Modal
      open={open && !isLoading}
      onOpenChange={onOpenChange}
      title={
        <FormattedMessage
          id="editTemplate.title"
          defaultMessage="Edit Report Template"
        />
      }
      description={
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="template.step"
              defaultMessage="Step {step} of 2"
              values={{ step }}
            />
          </p>
          <p className="text-sm text-muted-foreground">
            {step === EditTemplateStep.NAME_AND_DEFINITION ? (
              <FormattedMessage
                id="editTemplate.step1Description"
                defaultMessage="Update name and definition"
              />
            ) : (
              <FormattedMessage
                id="editTemplate.step2Description"
                defaultMessage="Update the instructions for the template"
              />
            )}
          </p>
        </div>
      }
      content={
        <form
          onSubmit={handleSubmit}
          id="edit-template-form"
          className="space-y-6"
        >
          {renderStepContent()}
        </form>
      }
      footer={renderFooter()}
    />
  );
}
